// src/components/Dashboard/ProjectsQuickAccess.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Box,
  Link,
  Tabs,
  Tab,
} from '@mui/material';
import { format } from 'date-fns';
import {
  getProjectsQuickAccess,
  ProjectsQuickAccessFilters,
  DashboardApiError,
} from '../../services/dashboardApi';
import { isIntegerUserId } from '../../services/authApi';

interface ProjectsQuickAccessProps {
  type: 'user' | 'airport' | 'system';
  userId?: number | string; // May be GUID (string) or integer (number)
  airportId?: number;
  /** Hide link to full project portfolio (/builder) — Tenant/Client home */
  hidePortfolioLink?: boolean;
}

const SHOW_IN_PROGRESS_TABS = true; // Tenant and Airport Manager get All | In Progress tabs, default In Progress

export default function ProjectsQuickAccess({
  type,
  userId,
  airportId,
  hidePortfolioLink = false,
}: ProjectsQuickAccessProps) {
  const navigate = useNavigate();
  const showTabs = (type === 'user' || type === 'airport') && SHOW_IN_PROGRESS_TABS;
  const [portfolioView, setPortfolioView] = useState<'all' | 'inProgress'>(() =>
    showTabs ? 'inProgress' : 'all'
  );
  const [projects, setProjects] = useState<
    Awaited<ReturnType<typeof getProjectsQuickAccess>>['projects']
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);

        // For airport type, wait for airportId when showing "All" (when "In Progress" we use /api/projects without airportId)
        if (type === 'airport' && portfolioView === 'all' && !airportId) {
          setLoading(true);
          return;
        }

        // Skip API call if userId is a GUID (backend should return integer, but may fallback to GUID)
        if (type === 'user' && userId && typeof userId === 'string') {
          setError('Projects quick access unavailable: userId format not supported (GUID fallback)');
          setLoading(false);
          return;
        }

        const filters: ProjectsQuickAccessFilters = {
          page: 1,
          pageSize: 10, // Limit to 10 items
        };

        if (showTabs && portfolioView === 'inProgress') {
          filters.inProgressAssignmentsOnly = true;
        }
        if (type === 'user' && userId && isIntegerUserId(userId)) {
          filters.userId = userId;
        } else if (type === 'airport' && portfolioView === 'all' && airportId) {
          filters.airportId = airportId;
        }
        // When airport + In Progress: no airportId, backend filters by current user's in-progress assignments

        const response = await getProjectsQuickAccess(filters);
        const projectsArray = response.projects || [];
        const sorted = [...projectsArray].sort(
          (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
        );
        setProjects(sorted);
      } catch (err) {
        const apiError = err as DashboardApiError;
        setError(apiError.message || 'Failed to load projects');
        console.error('Error loading projects:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [type, userId, airportId, portfolioView, showTabs]);

  const handleRowClick = (projectId: number) => {
    // Find the project to check its status
    const project = projects.find(p => p.projectId === projectId);
    if (project) {
      const status = project.status?.toUpperCase();
      if (status === 'PLANNING') {
        navigate(`/planning?projectId=${projectId}`);
      } else if (status === 'EXECUTION' || status === 'COMPLETE') {
        navigate(`/execution?projectId=${projectId}`);
      } else {
        // Default to planning view for unknown status
        navigate(`/planning?projectId=${projectId}`);
      }
    } else {
      // Fallback if project not found
      navigate(`/execution?projectId=${projectId}`);
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'COMPLETE':
        return 'success';
      case 'EXECUTION':
        return 'primary';
      case 'PLANNING':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">Recent Projects</Typography>
          {showTabs && (
            <Tabs
              value={portfolioView}
              onChange={(_, v: 'all' | 'inProgress') => setPortfolioView(v)}
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36 } }}
            >
              <Tab label="In Progress" value="inProgress" />
              <Tab label="All" value="all" />
            </Tabs>
          )}
          <Chip label={`${projects.length} projects`} size="small" color="primary" variant="outlined" />
        </Box>
        {!hidePortfolioLink && (
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/builder')}
          sx={{ cursor: 'pointer' }}
        >
          View Projects
        </Link>
        )}
      </Box>
      
      {/* Projects Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Project Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No projects found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              projects.map((project) => (
                <TableRow
                  key={project.projectId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(project.projectId)}
                >
                  <TableCell>
                    <Typography variant="body2">{project.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={project.status.replace('_', ' ')}
                      size="small"
                      color={getStatusColor(project.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(project.lastUpdated), 'MMM dd, yyyy')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

